import { cellCenter, touchGrid } from '../grid.ts';
import { iatan2, idiv, imax, mulBp, withinRange } from '../math/fixed.ts';
import { rngRange } from '../math/rng.ts';
import type { BattleState, CartBeetle, CartsRules, CartsState, Enemy, Hero, SendaNpc } from '../types.ts';
import { damageEnemy, damageHero, ev, spawnEnemy } from './combat.ts';
import { stepToward } from './enemies.ts';
import { orderMove } from './heroes.ts';
import { pulseLand } from './brasa.ts';
import { inArc } from './golems.ts';
import { shootTick } from './workshop.ts';

/**
 * The carts (docs/sendas/artifice.md, chapter 4, act 2, «Las vagonetas»; the ground in battle/ojos-layout.ts). Up the
 * glacier's tongue into the dwarven mine: a gallery from the foot to the drill hall's door, three rails across it. Ink
 * beetles sleep on each crossing, their shells shut: a blow gets `shellBp` through, and nobody walks past them (the
 * rail's row is shut while one sleeps there, its slope too: no way round). A cart froze at the top of each rail's slope: a tap on its brake
 * sends the hero there, and standing by it `brake` lets the cart go. It runs its rail at `pace` and strikes everything
 * on it once: a beetle's shell cracks (it wakes), a nightmare takes `foeBp` of its life, the hero `heroBp`, Dunna two
 * blows. Each runs once: spent, a new one is built at the top `again` (40 s) later. Asleep, a beetle only turns its
 * head (it shuts the way); cracked, it wakes, walks for the nearer of the hero and Dunna and slams what is within its
 * `reach` (warned `warn`, 3 s). Past the second crossing a beetle awake and shut in its shell comes down from its
 * stretch's hole: only a cart cracks it (bring it onto an open rail and let its cart go again). The ice cracks (`crack`: spots warned 3 s, the first on the hero) and shades come down the
 * stretch ahead (`foes`). Won with the hero at the drill hall's door; lost with Dunna or the hero down.
 *
 * Cost: three carts walked along their rails (a check of the few things within reach as they go); the beetles move by
 * this module (a handful).
 */

const fresh = (r: CartsRules): CartsState => ({
  carts: r.rails.map(() => ({ mode: 0, at: 0, t: 0, hit: [], cracked: false })),
  beetles: [],
  job: -1,
  held: 0,
  open: 0,
  foe: r.foes.first,
  crack: r.crack.first,
  spots: [],
  end: -1,
  shot: 0,
  shellHit: false,
});

/** The mine's beetle with this id (null: not one of them). */
export const cartBeetle = (s: BattleState, id: number): CartBeetle | null => s.senda?.carts?.beetles.find((b) => b.id === id) ?? null;

/** Where a cart is now (on its rail, sim units along it): a point between its cells. */
export const cartPoint = (r: CartsRules, rail: number, at: number): { x: number; y: number } => {
  const p = r.rails[rail]?.path ?? [];
  const i = idiv(at, 1000);
  const a = p[i] ?? p[p.length - 1] ?? { x: 0, y: 0 };
  const b = p[i + 1] ?? a;
  const k = at - i * 1000;
  return { x: a.x + idiv((b.x - a.x) * k, 1000), y: a.y + idiv((b.y - a.y) * k, 1000) };
};

/** The crossing's cells shut while a beetle sleeps there in its shell; open crossings counted from the foot. */
const gates = (s: BattleState, r: CartsRules, w: CartsState, cue: (c: string) => void): void => {
  const shut = r.rails.map((_, i) => w.beetles.some((b) => b.rail === i && b.shell && s.enemies.some((e) => e.id === b.id && e.hp > 0)));
  let open = 0;
  while (open < shut.length && !shut[open]) open++;
  const plugs: number[] = [];
  shut.forEach((x, i) => {
    if (x) plugs.push(...(r.rails[i]?.cells ?? []));
  });
  plugs.sort((a, b) => a - b);
  const had = s.plugs ?? [];
  if (had.length !== plugs.length || had.some((c, i) => c !== plugs[i])) {
    s.plugs = plugs;
    touchGrid(s);
  }
  while (w.open < open) {
    w.open++;
    cue(`gate${w.open}`);
    const at = r.rails[w.open - 1]?.gate[0];
    if (at !== undefined) {
      const c = cellCenter(s.map.radius, at);
      ev(s, { t: 'senda', kind: 'free', npc: '', x: c.x, y: c.y, n: w.open });
    }
    // Past the second crossing, a beetle awake in its shell comes down from its stretch's hole (only a cart cracks it).
    const hole = w.open === 2 ? r.holes.find((h) => h.stretch === 2) : undefined;
    if (hole) beetle(s, r, w, -1, hole.x, hole.y);
  }
};

const beetle = (s: BattleState, r: CartsRules, w: CartsState, rail: number, x: number, y: number): void => {
  const e = spawnEnemy(s, 'beetle', 1, false, 0, x, y);
  const hp = imax(1, mulBp(mulBp(e.maxHp, s.rules?.enemyHpBp ?? 10000), r.beetle.hpBp));
  e.hp = e.maxHp = hp;
  // (Its shell is this module's: the common one would crack at the first heavy blow.)
  e.cracked = true;
  e.anim = rail >= 0 ? 'idle' : 'walk';
  e.facing = 256;
  w.beetles.push({ id: e.id, rail, shell: true, wind: -1, aim: 0, next: s.tick + 24 });
  w.beetles.sort((a, b) => a.id - b.id);
};

/** A tap on a brake: the hero goes to it. */
export const cartOrder = (s: BattleState, h: Hero, at: number): void => {
  const r = s.rules?.senda?.carts;
  const w = s.senda?.carts;
  if (!r || !w || w.end >= 0 || at < 0 || at >= r.rails.length) return;
  w.job = at;
  w.held = 0;
  const c = cellCenter(s.map.radius, r.rails[at]?.brake as number);
  orderMove(s, h, c.x, c.y);
  h.target = 0;
  h.forced = false;
};

const heroWork = (s: BattleState, r: CartsRules, w: CartsState): void => {
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (!h || w.job < 0) return;
  const c = cellCenter(s.map.radius, r.rails[w.job]?.brake as number);
  if (h.moving || !withinRange(h.x, h.y, c.x, c.y, r.reach)) {
    w.held = 0;
    if (!h.moving) w.job = -1;
    return;
  }
  if (++w.held < r.brake) return;
  const i = w.job;
  w.job = -1;
  w.held = 0;
  const cart = w.carts[i];
  if (!cart || cart.mode !== 0) {
    ev(s, { t: 'senda', kind: 'brake', npc: '', x: c.x, y: c.y, n: -1 });
    return;
  }
  cart.mode = 1;
  cart.at = 0;
  cart.hit = [];
  cart.cracked = false;
  ev(s, { t: 'senda', kind: 'brake', npc: '', x: c.x, y: c.y, n: i });
  const p = r.rails[i]?.path[0] ?? c;
  ev(s, { t: 'senda', kind: 'cartGo', npc: '', x: p.x, y: p.y, n: i });
};

/** The carts running: what is on the rail where each one is, struck once. */
const cartsRun = (s: BattleState, r: CartsRules, w: CartsState, cue: (c: string) => void): void => {
  w.carts.forEach((cart, i) => {
    const rail = r.rails[i];
    if (!rail) return;
    if (cart.mode === 2 && s.tick >= cart.t) {
      cart.mode = 0;
      ev(s, { t: 'senda', kind: 'cartBuilt', npc: '', x: rail.path[0]?.x ?? 0, y: rail.path[0]?.y ?? 0, n: i });
    }
    if (cart.mode !== 1) return;
    cart.at += r.pace;
    const end = (rail.path.length - 1) * 1000;
    const p = cartPoint(r, i, cart.at > end ? end : cart.at);
    for (const e of s.enemies) {
      if (e.hp <= 0 || cart.hit.includes(e.id) || !withinRange(e.x, e.y, p.x, p.y, 650)) continue;
      cart.hit.push(e.id);
      const b = cartBeetle(s, e.id);
      if (b?.shell) {
        b.shell = false;
        cart.cracked = true;
        e.anim = 'walk';
        ev(s, { t: 'senda', kind: 'shellCrack', npc: '', x: e.x, y: e.y });
        cue('cart1');
      }
      ev(s, { t: 'senda', kind: 'cartHit', npc: '', x: e.x, y: e.y, n: i });
      damageEnemy(s, e, imax(1, mulBp(e.maxHp, r.foeBp)), { pid: '', pierce: true });
    }
    for (const h of s.heroes) {
      if (h.downTicks > 0 || cart.hit.includes(-1) || !withinRange(h.x, h.y, p.x, p.y, 600)) continue;
      cart.hit.push(-1);
      ev(s, { t: 'senda', kind: 'cartHit', npc: '', x: h.x, y: h.y, n: i });
      damageHero(s, h, mulBp(h.stats.maxHp, r.heroBp), null, 'physical', true);
    }
    const d = s.senda?.npcs.find((n) => n.id === 'dunna' && n.hp > 0);
    if (d && !cart.hit.includes(-2) && withinRange(d.x, d.y, p.x, p.y, 600)) {
      cart.hit.push(-2);
      d.hp = imax(0, d.hp - 2);
      ev(s, { t: 'senda', kind: d.hp > 0 ? 'hit' : 'down', npc: 'dunna', x: d.x, y: d.y });
    }
    if (cart.at >= end) {
      cart.mode = 2;
      cart.t = s.tick + r.again;
      ev(s, { t: 'senda', kind: 'cartSpent', npc: '', x: p.x, y: p.y, n: i });
      if (!cart.cracked) cue('spent');
    }
  });
};

/** The ice cracking: spots warned, the first on the hero, the rest along the gallery near it. */
const crackTick = (s: BattleState, r: CartsRules, w: CartsState, cue: (c: string) => void): void => {
  if (s.waveT < w.crack || w.end >= 0) return;
  w.crack = s.waveT + r.crack.every;
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (!h) return;
  const t = s.tick + r.crack.warn;
  const add = (x: number, y: number): void => {
    w.spots.push({ x, y, t });
    ev(s, { t: 'senda', kind: 'pulseWarn', npc: '', x, y, n: r.crack.warn });
  };
  add(h.x + rngRange(s.rng, -300, 300), h.y + rngRange(s.rng, -300, 300));
  for (let k = 1; k < r.crack.n; k++) {
    // A point of the gallery a few rows from the hero, ahead or behind.
    let best = 0;
    let bd = 0x7fffffff;
    r.way.forEach((p, i) => {
      const d = p.y > h.y ? p.y - h.y : h.y - p.y;
      if (d < bd) {
        bd = d;
        best = i;
      }
    });
    const p = r.way[imax(0, best + rngRange(s.rng, -3, 3))] ?? r.way[best];
    if (p) add(p.x + rngRange(s.rng, -900, 900), p.y);
  }
  cue('crack');
};

/** Shades down the stretch ahead (from its hole, or the gallery's foot of it). */
const foesTick = (s: BattleState, r: CartsRules, w: CartsState): void => {
  if (s.waveT < w.foe || w.end >= 0) return;
  w.foe = s.waveT + r.foes.every;
  const hole = r.holes.find((h) => h.stretch === w.open);
  // (The first stretch has no hole: they come from just short of its crossing.)
  const gate = r.rails[w.open];
  const near = gate ? cellCenter(s.map.radius, gate.gate[0] as number) : r.door;
  const at = hole ?? { x: near.x, y: near.y + 1700 };
  for (let k = 0; k < r.foes.n; k++) {
    const e = spawnEnemy(s, 'wraith', 1, false, 0, at.x + rngRange(s.rng, -300, 300), at.y + rngRange(s.rng, -200, 200));
    if (s.rules?.enemyHpBp) e.hp = e.maxHp = imax(1, mulBp(e.maxHp, s.rules.enemyHpBp));
  }
  ev(s, { t: 'senda', kind: 'holeOut', npc: '', x: at.x, y: at.y, n: r.foes.n });
};

export const cartsTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.carts;
  const st = s.senda;
  if (!r || !st) return;
  const w = (st.carts ??= fresh(r));
  if (!s.started) return;
  if (!w.beetles.length) r.rails.forEach((rl, i) => rl.beetles.forEach((b) => beetle(s, r, w, i, b.x, b.y)));
  gates(s, r, w, cue);
  if (w.shellHit) {
    w.shellHit = false;
    cue('shell');
  }
  if (w.end < 0) heroWork(s, r, w);
  cartsRun(s, r, w, cue);
  crackTick(s, r, w, cue);
  pulseLand(s, w.spots, r.crack);
  foesTick(s, r, w);
  const d = st.npcs.find((n): n is SendaNpc => n.id === 'dunna' && n.hp > 0);
  if (d) w.shot = shootTick(s, d, r.dunna, w.shot);
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (w.end < 0 && h && w.open >= r.rails.length && withinRange(h.x, h.y, r.door.x, r.door.y, r.doorR)) {
    w.end = s.tick;
    ev(s, { t: 'senda', kind: 'mouth', npc: '', x: r.door.x, y: r.door.y });
    cue('hall');
  }
};

/** A beetle of the mine (from the enemies' tick; true: it's one, nothing else moves it). */
export const cartsHold = (s: BattleState, e: Enemy, px: number): boolean => {
  const r = s.rules?.senda?.carts;
  const b = cartBeetle(s, e.id);
  if (!r || !b || e.hp <= 0) return false;
  const B = r.beetle;
  if (b.wind >= 0) {
    e.facing = b.aim;
    e.anim = 'cast';
    if (s.tick >= b.wind) {
      b.wind = -1;
      b.next = s.tick + B.rest;
      e.anim = 'attack';
      e.animT = 0;
      ev(s, { t: 'senda', kind: 'slam', npc: '', x: e.x, y: e.y, n: b.aim });
      for (const h of s.heroes) if (h.downTicks === 0 && inArc(e.x, e.y, b.aim, B.reach, B.arc, h.x, h.y)) damageHero(s, h, mulBp(h.stats.maxHp, B.heroBp), e, 'physical', true);
      for (const n of s.senda?.npcs ?? []) {
        if (n.hp <= 0 || n.away || !inArc(e.x, e.y, b.aim, B.reach, B.arc, n.x, n.y)) continue;
        n.hp = imax(0, n.hp - B.blows);
        n.flinch = 12;
        ev(s, { t: 'senda', kind: n.hp > 0 ? 'hit' : 'down', npc: n.id, x: n.x, y: n.y });
      }
    }
    return true;
  }
  if (e.anim === 'attack' && e.animT > 8) e.anim = b.rail >= 0 && b.shell ? 'idle' : 'walk';
  // Who it goes for: the nearer of the hero and Dunna.
  const h = s.heroes.find((x) => x.downTicks === 0);
  const d = s.senda?.npcs.find((n) => n.id === 'dunna' && n.hp > 0);
  const hd = h ? (h.x - e.x) * (h.x - e.x) + (h.y - e.y) * (h.y - e.y) : 0x7fffffff;
  const dd = d ? (d.x - e.x) * (d.x - e.x) + (d.y - e.y) * (d.y - e.y) : 0x7fffffff;
  const to = hd <= dd ? h : d;
  if (!to) return true;
  // Asleep in its shell on the rail: it turns its head to what comes near, and stays (it strikes nothing).
  if (b.rail >= 0 && b.shell) {
    if (withinRange(e.x, e.y, to.x, to.y, B.wake)) e.facing = iatan2(to.y - e.y, to.x - e.x);
    return true;
  }
  if (s.tick >= b.next && withinRange(e.x, e.y, to.x, to.y, B.reach - 200)) {
    b.aim = iatan2(to.y - e.y, to.x - e.x);
    b.wind = s.tick + B.warn;
    e.facing = b.aim;
    e.anim = 'cast';
    ev(s, { t: 'senda', kind: 'slamWarn', npc: '', x: e.x, y: e.y, n: b.aim });
    return true;
  }
  if (withinRange(e.x, e.y, to.x, to.y, B.reach - 400)) {
    e.anim = 'idle';
    e.facing = iatan2(to.y - e.y, to.x - e.x);
    return true;
  }
  e.anim = 'walk';
  stepToward(e, to.x, to.y, px);
  return true;
};

/** A blow on a beetle in its shell: `shellBp` of it gets through (and the first one is said). */
export const cartsTakes = (s: BattleState, e: Enemy, dmg: number): number => {
  const r = s.rules?.senda?.carts;
  const b = cartBeetle(s, e.id);
  if (!r || !b || !b.shell) return dmg;
  if (s.tick % 6 === 0) ev(s, { t: 'boom', x: e.x, y: e.y, r: 400, kind: 'blocked' });
  const w = s.senda?.carts;
  // (Its line, said by the act's tick.)
  if (w) w.shellHit = true;
  return mulBp(dmg, r.shellBp);
};

/** The three crossings open and the hero at the door. */
export const cartsGoal = (s: BattleState): { done: number; need: number } => {
  const r = s.rules?.senda?.carts;
  const w = s.senda?.carts;
  const n = r?.rails.length ?? 3;
  if (!w) return { done: 0, need: n + 1 };
  return { done: w.open + (w.end >= 0 ? 1 : 0), need: n + 1 };
};

export const cartsResult = (s: BattleState): 'won' | 'lost' | null => {
  const w = s.senda?.carts;
  if (!w) return null;
  return w.end >= 0 && s.tick >= w.end + 12 ? 'won' : null;
};
